import { useEffect, useState, useRef, CSSProperties, useMemo, useCallback } from 'react';
import { List, ListImperativeAPI } from 'react-window';
import Cell from './DataCell';
import DataSourceContextMenu from '../ContextMenu/ContextMenu';
import { RowInsertType } from '../../enums/RowInsertType';
import { EditableDataSource, DataSourceCell } from '../../types/Data/DataSource';

// Layout constants
const ROW_HEIGHT = 56; // 3.5em converted to pixels (assuming 16px base font)
const HEADER_HEIGHT = 75;
const COLUMN_MIN_WIDTH = 100; // absolute minimum width per column in pixels
const PADDING_PER_COLUMN = 40; // padding and borders
const VIEWPORT_OFFSET = 300; // offset from viewport height for table height calculation
const MAX_TABLE_HEIGHT_VH = 0.7; // maximum table height as percentage of viewport height (70vh)
const HEADER_FONT_SIZE = 16; // font size for header text measurement
const HEADER_FONT = `${HEADER_FONT_SIZE}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
const FALLBACK_CHAR_WIDTH = 8; // fallback character width estimation
const CONTEXT_MENU_ITEM_HEIGHT = 24;
const CONTEXT_MENU_ITEM_SPACING = 4;
const CONTEXT_MENU_ITEMS_COUNT = 5; // number of items in context menu (Above, Below, Clone, Delete, spacing)

const getDataHeaders = ({ headers }: EditableDataSource, data: DataSourceCell[][]) => (
    data[0].map((d) => d.columnIndex !== undefined ? headers[d.columnIndex] : { name: 'N/A', displayAt: '' })
);

interface DataSourceTableProps {
    dataSource: EditableDataSource;
    handleCellChange?: (data: DataSourceCell) => void;
    handleRowInsert?: (targetRow: number, type: RowInsertType) => void;
    handleRowDelete?: (targetRow: number) => void;
};

const initDataSourceContextMenuMetadata = {
    show: false,
    x: 0,
    y: 0,
}

const DataSourceTable = ({
    dataSource,
    handleCellChange = () => null,
    handleRowInsert = () => null,
    handleRowDelete = () => null,
}: DataSourceTableProps) => {
    const [dataSourceContextMenuMetadata, setDataSourceContextMenuMetadata] = useState(initDataSourceContextMenuMetadata);
    const [targetRow, setTargetRow] = useState(0);
    const [data, setData] = useState<DataSourceCell[][] | null>(null);
    const [editing, setEditing] = useState<boolean>(false);
    const [containerHeight, setContainerHeight] = useState(600);
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<ListImperativeAPI>(null);
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null); // Cached canvas for text measurement

    const tableOnChange = useCallback((data: DataSourceCell) => {
        if (handleCellChange) {
            handleCellChange(data);
        }
    }, [handleCellChange]);

    // Calculate header text width using cached canvas
    const measureTextWidth = useCallback((text: string): number => {
        // Use cached canvas to avoid memory leak
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
        }
        const context = canvasRef.current.getContext('2d');
        if (context) {
            // Match the font used in headers
            context.font = HEADER_FONT;
            return Math.ceil(context.measureText(text).width);
        }
        // Fallback: estimate based on character count
        return text.length * FALLBACK_CHAR_WIDTH;
    }, []);

    useEffect(() => {
        // If dataSource.rows is undefined, initialize data as empty array
        if (!dataSource.rows) {
            setData([]);
            return;
        }

        // Set data directly from dataSource.rows
        setData(dataSource.rows);

        // Determine if the data source is editable
        if (dataSource.rows.length > 0 && dataSource.rows[0].length > 0) {
            setEditing(dataSource.rows[0][0].editable);
        }
    }, [dataSource])

    // Set up resize observer for responsive container sizing
    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateDimensions();

        // Use ResizeObserver for better performance
        const resizeObserver = new ResizeObserver(() => {
            updateDimensions();
        });

        resizeObserver.observe(containerRef.current);

        // Also observe window resize for height calculation
        window.addEventListener('resize', updateDimensions);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    // Calculate container height based on data rows and viewport
    useEffect(() => {
        if (!data) return;

        // Calculate needed height based on number of rows
        const neededHeight = HEADER_HEIGHT + (data.length * ROW_HEIGHT);

        // Calculate max allowed height based on viewport
        const maxAllowedHeight = Math.min(
            window.innerHeight - VIEWPORT_OFFSET,
            window.innerHeight * MAX_TABLE_HEIGHT_VH
        );

        // Use the smaller of needed height or max allowed height
        const finalHeight = Math.min(neededHeight, maxAllowedHeight);

        setContainerHeight(finalHeight);
    }, [data]);

    // Re-measure container when data changes (e.g., switching tables)
    useEffect(() => {
        if (containerRef.current && data && data.length > 0) {
            // Small delay to ensure DOM has updated
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    setContainerWidth(containerRef.current.offsetWidth);
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [data]);

    // Memoize headers calculation
    const headers = useMemo(() => {
        return data && data.length > 0 ? getDataHeaders(dataSource, data) : dataSource.headers;
    }, [data, dataSource]);

    // Calculate minimum widths based on header text (memoized)
    const minColumnWidths = useMemo(() => {
        return headers.map(header => {
            const textWidth = measureTextWidth(header.name);
            return Math.max(COLUMN_MIN_WIDTH, textWidth + PADDING_PER_COLUMN);
        });
    }, [headers, measureTextWidth]);

    const minTotalWidth = useMemo(() => {
        return minColumnWidths.reduce((sum, width) => sum + width, 0);
    }, [minColumnWidths]);

    // Calculate final column widths: expand to fill container if there's space (memoized)
    const { finalColumnWidths, totalWidth } = useMemo(() => {
        let finalWidths: number[];
        let total: number;

        if (containerWidth > minTotalWidth && minTotalWidth > 0) {
            // Distribute extra space proportionally based on measured widths
            const ratio = containerWidth / minTotalWidth;
            finalWidths = minColumnWidths.map(width => Math.floor(width * ratio));

            // Adjust for rounding errors - add remaining pixels to last column
            const currentTotal = finalWidths.reduce((sum, width) => sum + width, 0);
            const remainder = containerWidth - currentTotal;
            if (remainder > 0 && finalWidths.length > 0) {
                finalWidths[finalWidths.length - 1] += remainder;
            }
            total = containerWidth;
        } else {
            // Use minimum widths (enables horizontal scroll if needed)
            finalWidths = minColumnWidths;
            total = minTotalWidth;
        }

        return { finalColumnWidths: finalWidths, totalWidth: total };
    }, [containerWidth, minTotalWidth, minColumnWidths]);

    const handleDataSourceContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>, row: number) => {
        e.preventDefault();
        const { pageX, pageY } = e;

        // account for the height of each of the entries and the space between them
        const height = (CONTEXT_MENU_ITEMS_COUNT * CONTEXT_MENU_ITEM_HEIGHT) + (3 * CONTEXT_MENU_ITEM_SPACING);

        setDataSourceContextMenuMetadata({
            show: true,
            x: pageX,
            y: pageY - height,
        });
        setTargetRow(row);
    }, []);

    const DataSourceContextMenuClose = useCallback(() => {
        setDataSourceContextMenuMetadata(initDataSourceContextMenuMetadata);
    }, []);

    const handleDelete = useCallback((targetRow: number) => {
        handleRowDelete(targetRow);
    }, [handleRowDelete]);

    const handleInsert = useCallback((targetRow: number, type: RowInsertType) => {
        handleRowInsert(targetRow, type);
    }, [handleRowInsert]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        if (headerScrollRef.current && e.currentTarget) {
            headerScrollRef.current.scrollLeft = (e.currentTarget as HTMLDivElement).scrollLeft;
        }
    }, []);

    // If no data, render nothing (parent will handle access control)
    if (!data || data.length === 0) {
        return null;
    }

    const editorDataSourceContextMenu = () => {
        const rowInsertClick = (type: RowInsertType) => {
            handleInsert(targetRow, type);
            DataSourceContextMenuClose();
        };

        const rowDeleteClick = () => {
            handleDelete(targetRow);
            DataSourceContextMenuClose();
        };

        return (
            <div className='p-2 rounded-3'>
                {<div className='my-1' onClick={() => rowInsertClick(RowInsertType.Above)}>insert row above</div>}
                {<div className='my-1' onClick={() => rowInsertClick(RowInsertType.Below)}>insert row below</div>}
                {<div className='my-1' onClick={() => rowInsertClick(RowInsertType.Clone)}>clone row</div>}
                {<div className='my-1' onClick={() => rowDeleteClick()}>delete row</div>}
            </div>
        )
    }

    return (
        <div ref={containerRef}>
            {dataSourceContextMenuMetadata.show &&
                <DataSourceContextMenu
                    x={dataSourceContextMenuMetadata.x}
                    y={dataSourceContextMenuMetadata.y}
                    closeConextMenu={DataSourceContextMenuClose}
                    className='border bg-light rounded-3 shadow-sm'
                >
                    {editing && editorDataSourceContextMenu()}
                </DataSourceContextMenu>
            }
            <div className='mt-2'>
                {/* Fixed Header */}
                <div
                    ref={headerScrollRef}
                    className='data-source-table'
                    style={{ overflowX: 'hidden', overflowY: 'hidden' }}
                >
                    <div className='text-bg-secondary text-center d-flex' style={{ height: `${HEADER_HEIGHT}px`, minWidth: `${totalWidth}px` }}>
                        {headers.map((header, i) => (
                            <div
                                key={`${header.name}${i}`}
                                className={`data-source-table-header ${i === 0 ? '' : 'border-start border-light'} px-2 d-flex align-items-center justify-content-center`}
                                style={{
                                    width: `${finalColumnWidths[i] || COLUMN_MIN_WIDTH}px`,
                                    minWidth: `${finalColumnWidths[i] || COLUMN_MIN_WIDTH}px`,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {header.name}
                            </div>
                        ))}
                    </div>
                </div>
                {/* Virtualized Body */}
                <List<VirtualRowCustomProps>
                    listRef={listRef}
                    rowCount={data?.length ?? 0}
                    rowHeight={ROW_HEIGHT}
                    style={{
                        width: '100%',
                        height: containerHeight
                    }}
                    onScroll={handleScroll}
                    rowComponent={VirtualRow}
                    rowProps={{
                        rowData: data,
                        headers,
                        onCellChange: tableOnChange,
                        onContextMenu: handleDataSourceContextMenu,
                        columnWidths: finalColumnWidths,
                        totalWidth
                    }}
                />
            </div>
        </div>
    );
}

// Virtual row component for react-window
// Custom props we pass through rowProps
interface VirtualRowCustomProps {
    rowData: DataSourceCell[][];
    headers: Array<{ name: string; displayAt: string }>;
    onCellChange: (data: DataSourceCell) => void;
    onContextMenu: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, row: number) => void;
    columnWidths: number[];
    totalWidth: number;
}

// Combined props including those provided by List component
interface VirtualRowProps extends VirtualRowCustomProps {
    index: number;
    style: CSSProperties;
    ariaAttributes: {
        "aria-posinset": number;
        "aria-setsize": number;
        role: "listitem";
    };
}

// VirtualRow component - memoization handled by react-window internally
const VirtualRow = ({ index, style, rowData, onCellChange, onContextMenu, ariaAttributes, columnWidths, totalWidth }: VirtualRowProps) => {
    const row = rowData[index];

    return (
        <div
            {...ariaAttributes}
            style={{
                ...style,
                minWidth: `${totalWidth}px`
            }}
            className={`d-flex ${(index % 2 === 1) ? 'bg-light' : ''}`}
            onContextMenu={(e) => onContextMenu(e, index)}
        >
            {row?.map((cellData, cellIndex) => (
                <div
                    key={`${cellData.rowIndex}${cellData.columnIndex}`}
                    style={{
                        width: `${columnWidths[cellIndex] || COLUMN_MIN_WIDTH}px`,
                        minWidth: `${columnWidths[cellIndex] || COLUMN_MIN_WIDTH}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Cell
                        data={cellData}
                        onChange={onCellChange}
                        asDiv={true}
                    />
                </div>
            ))}
        </div>
    );
};

export default DataSourceTable;